import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { IoMdCalendar } from 'react-icons/io'

import { Button } from '#/components/ui/button'
import {
  MobileNavOverlay,
  type NavLinkItem,
} from '#/components/layout/MobileNavOverlay'
import { cn } from '#/lib/utils'
import logo from '#/assets/logo-icon.png'
import logoWhite from '#/assets/logo-icon-yellow.png'

type NavbarProps = {
  className?: string
}

const NAV_LINKS: NavLinkItem[] = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  { label: 'Courses', to: '/courses' },
  { label: 'Plans', to: '/plans' },
  { label: 'Blog', to: '/blog' },
  { label: 'Contact Us', to: '/contact' },
]

type NavbarActionsProps = {
  className?: string
}

function NavbarActions({ className }: NavbarActionsProps) {
  const navigate = useNavigate()
  return (
    <Button
      className={cn('', className)}
      onClick={() => navigate({ to: '/subscribe' })}
    >
      <IoMdCalendar />
      Free Trial Class
    </Button>
  )
}

const pagesHasActiveNavbar: string[] = []

export default function Navbar({ className }: NavbarProps) {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })

  const [isScrolled, setIsScrolled] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const isActiveNavbar =
    isScrolled || pagesHasActiveNavbar.includes(pathname.split('/')[1])

  useEffect(() => {
    let rafId = 0

    const handleScroll = () => {
      if (rafId) return
      rafId = window.requestAnimationFrame(() => {
        rafId = 0
        const scrollTop = window.scrollY
        const docHeight =
          document.documentElement.scrollHeight - window.innerHeight
        const progress = docHeight > 0 ? scrollTop / docHeight : 0

        setIsScrolled(scrollTop > 12)
        setScrollProgress(Math.min(Math.max(progress, 0), 1))
      })
    }

    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
      if (rafId) window.cancelAnimationFrame(rafId)
    }
  }, [])

  return (
    <>
      <div
        className={cn(
          'left-0 top-0 fixed z-50 w-full py-5.5 transition-all duration-300 max-xl:flex-col max-xl:gap-5 max-md:py-2',
          isActiveNavbar
            ? ' bg-white py-4 shadow-[0_10px_30px_rgba(0,0,0,0.12)]'
            : ' bg-transparent',
          className,
        )}
      >
        <div className="absolute left-0 top-0 h-0.75 w-full overflow-hidden">
          <div
            className="h-full origin-left bg-primary-500 transition-[transform] ease-out duration-150"
            style={{ transform: `scaleX(${scrollProgress})` }}
          />
        </div>
        <div className="container mx-auto w-full items-center justify-between flex max-xl:items-center">
          <Link to="/" className="flex items-center gap-3">
            <img
              alt="Wa'allamah Institute logo object-contain"
              className="md:w-16 w-14 h-auto"
              fetchPriority="high"
              src={isActiveNavbar ? logo : logoWhite}
              width={64}
              height={64}
              decoding="async"
            />
            <h2
              className={cn(
                'md:text-xl font-bold leading-[normal]  transition-colors duration-200',
                isActiveNavbar ? 'text-neutral-900' : 'text-white',
              )}
            >
              Wa'allamah Institute
            </h2>
          </Link>

          <div className="hidden items-center gap-2 max-xl:hidden xl:flex">
            {NAV_LINKS.map((link) => {
              const isActive =
                link.to === '/'
                  ? pathname === '/'
                  : pathname.startsWith(link.to)

              return (
                <div
                  key={link.label}
                  className="flex items-center justify-center px-4 py-2.5"
                >
                  <Link
                    to={link.to}
                    className={cn(
                      'relative text-[16px] font-medium leading-[normal] transition-colors duration-200',
                      isActiveNavbar
                        ? 'text-[color:var(--neutral-700)] hover:text-[color:var(--neutral-900)]'
                        : 'text-white/85 hover:text-white',
                      'after:absolute after:left-1/2 after:-bottom-1.5 after:h-0.5 after:w-full after:-translate-x-1/2 after:scale-x-0 after:bg-[color:var(--primary-500)] after:transition-transform after:duration-200 after:origin-center',
                      isActive && 'text-primary-500 after:scale-x-100',
                      'hover:text-primary-500 hover:after:scale-x-100',
                    )}
                  >
                    {link.label}
                  </Link>
                </div>
              )
            })}
          </div>

          <div className="hidden items-center gap-4 xl:flex">
            <NavbarActions />
          </div>

          {/* ─── Awwwards-style Mobile Menu Trigger ─────────────────────── */}
          <div className="flex items-center gap-3 xl:hidden">
            <button
              type="button"
              aria-label="Open mobile navigation"
              aria-expanded={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(true)}
              className={cn(
                'group relative flex h-11 items-center gap-2.5 rounded-full px-4 py-2 border transition-all duration-300 cursor-pointer select-none active:scale-95',
                isActiveNavbar
                  ? 'border-neutral-200 bg-neutral-50/90 text-neutral-900 shadow-xs hover:border-primary-500/50 hover:bg-primary-50/50 hover:shadow-md'
                  : 'border-white/20 bg-white/10 text-white backdrop-blur-md hover:border-primary-400/60 hover:bg-white/20',
              )}
            >
              <span className="text-xs font-bold uppercase tracking-widest">
                Menu
              </span>
              <div className="flex flex-col items-end gap-1 w-4">
                <span
                  className={cn(
                    'h-0.5 w-4 rounded-full transition-all duration-300 group-hover:w-4',
                    isActiveNavbar
                      ? 'bg-neutral-900 group-hover:bg-primary-600'
                      : 'bg-white group-hover:bg-primary-400',
                  )}
                />
                <span
                  className={cn(
                    'h-0.5 w-2.5 rounded-full transition-all duration-300 group-hover:w-4',
                    isActiveNavbar ? 'bg-primary-600' : 'bg-primary-400',
                  )}
                />
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* ─── Fullscreen Awwwards Animated Mobile Navigation ───────────── */}
      <MobileNavOverlay
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        navLinks={NAV_LINKS}
      />
    </>
  )
}
